import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, RefreshCw, TriangleAlert } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { PayablesAccountingSection, ReceivablesAccountingSection } from "@/components/AccountingSettingsSections";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/lib/permissions";
import type { components } from "@dolfin/api-admin";

type AccountingConnectionResponse = components["schemas"]["AccountingConnectionResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];
type AccountingTaxRateResponse = components["schemas"]["AccountingTaxRateResponse"];

const CONNECTION_PATH = "/organisation/settings/accounting";

// The connection and its synced chart belong to the organisation; this page only reads them to offer choices.
export function AreaAccountingSettings({ area }: { area: "ar" | "ap" }) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canEdit = can("connectAccounting");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [connection, setConnection] = useState<AccountingConnectionResponse | null>(null);
  const [accounts, setAccounts] = useState<LedgerAccountResponse[]>([]);
  const [taxRates, setTaxRates] = useState<AccountingTaxRateResponse[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [connectionResult, accountsResult, taxResult] = await Promise.all([
      api.GET("/v1/accounting/connection", {}),
      api.GET("/v1/accounting/ledger-accounts", {}),
      api.GET("/v1/accounting/tax-rates", {}),
    ]);
    setLoadFailed(Boolean(connectionResult.error));
    setConnection(connectionResult.data ?? null);
    // Archived accounts and rates can no longer be posted to, so they are not offered.
    setAccounts((accountsResult.data ?? []).filter((a) => a.isActive));
    setTaxRates((taxResult.data ?? []).filter((r) => r.isActive));
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingState label="Loading your accounting connection..." />;
  }

  if (loadFailed) {
    return <ErrorState message="Couldn't load your accounting connection." onRetry={load} />;
  }

  const documents = area === "ar" ? "invoices" : "bills";
  const platformLabel = connection?.serviceDisplayName || connection?.serviceId || "your accounting platform";
  const manageConnection = (
    <Button asChild size="sm">
      <Link to={CONNECTION_PATH}>
        Go to Organisation &gt; Accounting <ArrowRight className="size-4" />
      </Link>
    </Button>
  );

  if (connection?.status === "Broken") {
    return (
      <EmptyState icon={TriangleAlert} title={`${platformLabel} needs reconnecting`} action={manageConnection}>
        Nothing posts to your books until the connection is repaired. Reconnect it from the organisation's
        accounting settings; your choices here are kept.
      </EmptyState>
    );
  }

  if (connection?.status !== "Callable") {
    return (
      <EmptyState icon={Landmark} title="No accounting platform connected" action={manageConnection}>
        Connect Xero or QuickBooks once for the whole organisation, then come back to choose where {documents} post.
      </EmptyState>
    );
  }

  if (accounts.length === 0) {
    return (
      <EmptyState icon={RefreshCw} title="No accounts synced yet" action={manageConnection}>
        Sync your chart of accounts from {platformLabel} before choosing where {documents} post.
      </EmptyState>
    );
  }

  return (
    <div className="@container space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card/60 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg border bg-muted/50">
            {connection.serviceIconUrl ? (
              <img src={connection.serviceIconUrl} alt="" className="size-5 object-contain" />
            ) : (
              <Landmark className="size-4 text-muted-foreground" />
            )}
          </span>
          <div className="min-w-0 text-sm">
            <div className="truncate font-semibold">{connection.companyName || platformLabel}</div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {connection.companyName ? `Connected in ${platformLabel}` : "Connected"}
            </div>
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={CONNECTION_PATH}>Manage connection</Link>
        </Button>
      </div>

      {area === "ar" ? (
        <ReceivablesAccountingSection
          accounts={accounts}
          taxRates={taxRates}
          platform={connection.serviceId}
          canEdit={canEdit}
        />
      ) : (
        <PayablesAccountingSection
          accounts={accounts}
          taxRates={taxRates}
          platform={connection.serviceId}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
